import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw,
  FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import MVRScanPreview, { MVRTransaction } from './MVRScanPreview';
const PAGE_SIZE       = 10;
const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';

const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
  0: { label: 'Submitted',     color: '#fbbf24' },
  1: { label: 'In Progress',   color: '#a78bfa' },
  2: { label: 'Done',          color: '#34d399' },
  3: { label: 'Failed',        color: '#f87171' },
  4: { label: 'Not Supported', color: '#9ca3af' },
};

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

const getClientName = (tx: MVRTransaction): string => {
  try {
    if (!tx.selectionOptionsJson) return '—';
    const p = JSON.parse(tx.selectionOptionsJson);
    return p.clientName || '—';
  } catch { return '—'; }
};

const getStatusBadge = (status: number | string) => {
  const code = typeof status === 'string' ? parseInt(status, 10) : status;
  const cfg  = STATUS_CONFIG[code];
  return cfg ? <span className="qp-status-pill" style={{ color: cfg.color }}>{cfg.label}</span> : null;
};

const AmsStatusBadge: React.FC<{ yes: boolean }> = ({ yes }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: yes ? '#d1fae5' : '#fee2e2',
    color:      yes ? '#065f46' : '#991b1b',
  }}>
    {yes ? 'Yes' : 'No'}
  </span>
);

const MVRScanHistory: React.FC = () => {
  const [rows,       setRows]       = useState<MVRTransaction[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview */
  const [previewTx, setPreviewTx] = useState<MVRTransaction | null>(null);

  /* Feedback */
  const [feedbackTxId,       setFeedbackTxId]       = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedBackError,      setFeedBackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess } = useToast();

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MVRScan/history?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        {
          headers: { Authorization: `Bearer ${getToolboxToken()}`, accept: 'application/json' },
          signal,
        },
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const data: MVRTransaction[] = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(
        Array.isArray(data) && data.length > 0
          ? (data[0].totalCount ?? data.length)
          : 0,
      );
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.fileName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      getClientName(r).toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  const openPreview = useCallback((tx: MVRTransaction) => {
    const raw = tx as any;

    // Fix Python-style llmResponse → valid JSON for driverDetailsJson
    let driverDetailsJson = tx.driverDetailsJson;
    if (!driverDetailsJson && raw.llmResponse) {
      driverDetailsJson = raw.llmResponse
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false');
    }

    // Resolve driverDatabaseId from selectedDriverData when driver is already in AMS
    let driverDatabaseId = tx.driverDatabaseId;
    if (!driverDatabaseId && tx.isDriverAddedInAMS && raw.selectedDriverData) {
      try {
        const arr = JSON.parse(raw.selectedDriverData);
        if (Array.isArray(arr) && arr[0]?.databaseId) {
          driverDatabaseId = arr[0].databaseId;
        }
      } catch { /* ignore */ }
    }

    // Build selectionOptionsJson — map insuredDatabaseId → insuredClientId
    let selectionOptionsJson = tx.selectionOptionsJson;
    if (raw.selectionOptions) {
      try {
        const opts = JSON.parse(raw.selectionOptions);
        selectionOptionsJson = JSON.stringify({
          ...opts,
          insuredClientId: opts.insuredClientId || opts.insuredDatabaseId || '',
        });
      } catch { /* ignore */ }
    }

    setPreviewTx({ ...tx, driverDetailsJson, driverDatabaseId, selectionOptionsJson });
  }, []);

  const onRefreshRow = useCallback((txId: string) => {
    setRows(prev =>
      prev.map(r => r.transactionId === txId
        ? { ...r, isDriverAddedInAMS: true, isViolationAddedInAMS: true }
        : r,
      ),
    );
  }, []);

  /* ── Feedback ── */
  const openFeedback = useCallback((tx: MVRTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(tx.transactionId);
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const saveRating = useCallback((txId: string, isLiked: boolean | null, feedback: string) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 9, TransactionId: txId, IsLiked: isLiked, Feedback: feedback ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { /* silent */ });
  }, [closeFeedback]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur  = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, rows, feedbackText, saveRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur  = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, rows, feedbackText, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Feedback must be under 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 9, TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => setFeedbackSubmitting(false));
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  const isDone = (tx: MVRTransaction) => {
    const code = typeof tx.status === 'string' ? parseInt(tx.status, 10) : tx.status;
    return code === 2;
  };

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>MVR Scan History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiFileText size={28} />
              <p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching MVR scan records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search.' : 'No MVR scans yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">File Name</th>
                    <th className="qp-table__th">Date</th>
                    <th className="qp-table__th">User</th>

                    <th className="qp-table__th">Status</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Driver in AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Violation in AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tx, i) => (
                    <tr key={tx.transactionId ?? i} className="qp-table__row">
                      <td
                        className="qp-table__td"
                        style={{
                          color: 'var(--accent-purple-light)', fontWeight: 500,
                          maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {tx.fileName}
                      </td>
                      <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(tx.created ?? '')}
                      </td>
                      <td className="qp-table__td">{tx.userName || '—'}</td>

                      <td className="qp-table__td">{getStatusBadge(tx.status)}</td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <AmsStatusBadge yes={!!tx.isDriverAddedInAMS} />
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <AmsStatusBadge yes={!!tx.isViolationAddedInAMS} />
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <div className="qp-table__actions">
                          {isDone(tx) ? (
                            <button
                              className="qp-table__action-btn"
                              title="Preview"
                              onClick={() => openPreview(tx)}
                            >
                              <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                              Not Available
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button
                          className="qp-table__action-btn"
                          title="Feedback"
                          onClick={e => openFeedback(tx, e)}
                          style={{ margin: '0 auto' }}
                        >
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`ell-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Feedback Popup */}
      {feedbackTxId && feedbackAnchor && (
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedback}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTx && (
        <MVRScanPreview
          tx={previewTx}
          onClose={() => setPreviewTx(null)}
          onAMSUpdate={() => {
            onRefreshRow(previewTx.transactionId);
            fetchData(page);
          }}
        />
      )}
    </>
  );
};

export default MVRScanHistory;

